"use client";

import { memo, useMemo, useRef, type FC } from "react";
import { useAuiState } from "@assistant-ui/react";
import { MessageCircleQuestionIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HYBRID_WAIT_TOOL_NAMES } from "@/lib/toolConstants";
import type { UiPart } from "@/components/custom/chatModelAdapters";

// ─── Types ──────────────────────────────────────────────────────────────────────

type StructuredQA = {
  questionId: string;
  question: string;
  answer: string | null;
};

type QuestionSet = {
  key: string;
  entries: StructuredQA[];
};

const EMPTY_CONTENT: readonly UiPart[] = [];

// ─── Helpers ────────────────────────────────────────────────────────────────────

/** Sentinels written by the non-continuation path — not displayable answers. */
const NON_DISPLAY_SENTINELS = new Set(["User answered", "User skipped"]);

/**
 * Attempts to parse the structured JSON answer format produced by
 * BuildStructuredAnswerJson. Falls back to null if parsing fails or
 * the format doesn't match.
 */
function parseStructuredAnswer(result: unknown): StructuredQA[] | null {
  const raw = typeof result === "string" ? result : null;
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    if (parsed.length === 0) return null;
    // Validate shape: each entry must have questionId, question, answer
    const valid = parsed.every(
      (e: unknown) =>
        typeof e === "object" &&
        e !== null &&
        "questionId" in e &&
        "question" in e &&
        "answer" in e,
    );
    if (!valid) return null;
    return parsed as StructuredQA[];
  } catch {
    return null;
  }
}

/**
 * Legacy fallback: extracts a single question/answer pair from the old
 * flat format (semicolon-joined answers, single question from args).
 */
function buildLegacyEntries(
  result: unknown,
  argsText: string | undefined,
): StructuredQA[] | null {
  const raw =
    typeof result === "string"
      ? result
      : result && typeof result === "object" && "answer" in result
        ? String((result as { answer: unknown }).answer)
        : "";
  if (!raw || NON_DISPLAY_SENTINELS.has(raw)) return null;

  // Try to extract question text from args
  let questionText = "?";
  if (argsText) {
    try {
      const parsed = JSON.parse(argsText);
      const questions = parsed?.questions;
      if (Array.isArray(questions) && questions.length > 0) {
        questionText =
          questions[0]?.Question ??
          questions[0]?.question ??
          questions[0]?.Header ??
          "?";
      }
    } catch {
      // malformed
    }
  }

  // Resolve option IDs to labels if possible
  let answerText = raw;
  if (argsText) {
    try {
      const parsed = JSON.parse(argsText);
      const questions: Array<{
        options?: Array<{ id: string; label: string }>;
      }> = parsed?.questions ?? [];
      const labelMap = new Map<string, string>();
      for (const q of questions) {
        for (const opt of q.options ?? []) {
          if (opt.id && opt.label) labelMap.set(opt.id, opt.label);
        }
      }
      if (labelMap.size > 0) {
        const parts = raw.split(/[;,]\s*/);
        answerText = parts
          .map((p) => labelMap.get(p.trim()) ?? p.trim())
          .join(", ");
      }
    } catch {
      // keep raw
    }
  }

  return [{ questionId: "q0", question: questionText, answer: answerText }];
}

function getQuestionTrailSignature(content: readonly UiPart[]): string {
  let signature = "";
  for (const part of content) {
    if (part.type !== "tool-call") continue;
    if (!HYBRID_WAIT_TOOL_NAMES.has(part.toolName)) continue;
    signature += `${part.toolCallId}:${part.state ?? ""}:${String(part.result ?? "")};`;
  }
  return signature;
}

// ─── Component ──────────────────────────────────────────────────────────────────

const QuestionsTrailImpl: FC = () => {
  const { t } = useTranslation();
  const stableContentRef = useRef<readonly UiPart[]>(EMPTY_CONTENT);
  const signatureRef = useRef("");

  const content = useAuiState(
    ({ message }) => {
      const nextContent = (message.content ?? EMPTY_CONTENT) as readonly UiPart[];
      const nextSignature = getQuestionTrailSignature(nextContent);
      if (nextSignature !== signatureRef.current) {
        signatureRef.current = nextSignature;
        stableContentRef.current = nextContent;
      }
      return stableContentRef.current;
    },
  );

  const questionSets = useMemo<QuestionSet[]>(() => {
    const sets: QuestionSet[] = [];
    for (const part of content) {
      if (part.type !== "tool-call") continue;
      if (!HYBRID_WAIT_TOOL_NAMES.has(part.toolName)) continue;
      if (part.state !== "complete") continue;

      // Try structured format first, then legacy fallback
      const entries =
        parseStructuredAnswer(part.result) ??
        buildLegacyEntries(part.result, part.argsText);
      if (!entries || entries.length === 0) continue;

      sets.push({ key: part.toolCallId, entries });
    }
    return sets;
  }, [content]);

  const totalQuestions = useMemo(
    () => questionSets.reduce((sum, s) => sum + s.entries.length, 0),
    [questionSets],
  );

  if (questionSets.length === 0) return null;

  return (
    <div className="aui-questions-trail mb-3 px-1 py-2">
      {/* Header */}
      <div className="flex items-center gap-1.5 pb-2">
        <MessageCircleQuestionIcon className="size-3.5 text-muted-foreground" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {t("questions.trail_heading")} ({totalQuestions})
        </span>
      </div>

      {/* Question sets */}
      <div className="flex flex-col">
        {questionSets.map((set, setIdx) => (
          <div key={set.key}>
            {/* Separator between question sets */}
            {setIdx > 0 && (
              <div className="my-1.5 border-t border-border/50" />
            )}

            {/* Stacked Q/A pairs within this set */}
            <div className="flex flex-col gap-0.5">
              {set.entries.map((entry, entryIdx) => (
                <div key={entry.questionId} className="min-w-0">
                  {/* Question */}
                  <div className="truncate text-[12px] leading-[18px] text-muted-foreground">
                    {set.entries.length > 1 && `${entryIdx + 1}) `}
                    {entry.question}
                  </div>
                  {/* Answer */}
                  <div className="truncate text-[12px] leading-[18px] text-foreground/80 font-medium">
                    {entry.answer ?? "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const QuestionsTrail = memo(QuestionsTrailImpl);
QuestionsTrail.displayName = "QuestionsTrail";
